import React from 'react';
import { connect } from 'react-redux';
import { string, object, array, oneOfType } from 'prop-types';
import Helmet from 'react-helmet';
import _sample from 'lodash/sample';

import { DEFAULT_LOCALE } from '@snapask/common-components/dist/constants/locales';

import config from '../../config';
import { getLanguage, getLocaleCode } from '../../redux/locale';

const {
  SNAPASK_SITE_ENDPOINT: SNAPASK_SITE_URL,
  FB_APP_ID,
  GOOGLE_SITE_VERIFICATION,
} = config;

const seoAssetPath = path => `${SNAPASK_SITE_URL}/static/seo/${path}`;

const seoImages = {
  openGraph: ['Home_SEO.jpg'],
  twitter: ['Home_SEO.jpg'],
  google: ['Home_SEO.jpg'],
};

const naverVerificationList = [
  '8055a730a4c7186c5135b823ee85ed5fff226360',
  'd861934470c2fc3b5598de1a52ed5f410cf71a9f',
];

const getMetaTags = ({
  title,
  description,
  seoImage,
  path,
  contentType,
  published,
  updated,
  category,
  tags,
  urlLocale,
  robots,
}) => {
  const naverSiteVerificationMetaTags = naverVerificationList.map(
    naverVerification => ({
      name: 'naver-site-verification',
      content: naverVerification,
    })
  );

  const metaTags = [
    /* eslint-disable object-property-newline */
    { charset: 'utf-8' },
    { 'http-equiv': 'X-UA-Compatible', content: 'IE=edge' },
    { itemprop: 'name', content: title },
    { itemprop: 'description', content: description },
    { itemprop: 'image', content: seoImage || seoAssetPath('Home_SEO.jpg') },
    {
      name: 'viewport',
      content: 'width=device-width, initial-scale=1.0, minimum-scale=1.0',
    },
    { name: 'robots', content: robots },
    { name: 'url', content: `${SNAPASK_SITE_URL}/${urlLocale}/${path}` },
    { name: 'description', content: description },
    { name: 'twitter:title', content: `${title}` },
    { name: 'twitter:description', content: description },
    {
      name: 'twitter:image:src',
      content: seoImage || seoAssetPath(_sample(seoImages.twitter)),
    },
    ...naverSiteVerificationMetaTags,
    { property: 'og:title', content: `${title}` },
    { property: 'og:type', content: contentType },
    { property: 'og:url', content: `${SNAPASK_SITE_URL}/${urlLocale}/${path}` },
    {
      property: 'og:image',
      content: seoImage || seoAssetPath(_sample(seoImages.openGraph)),
    },
    { property: 'og:description', content: description },
    { property: 'og:site_name', content: 'Snapask' },
    { property: 'fb:app_id', content: FB_APP_ID },
    { name: 'google-site-verification', content: GOOGLE_SITE_VERIFICATION },
  ];

  if (published)
    metaTags.push({ name: 'article:published_time', content: published });
  if (updated)
    metaTags.push({ name: 'article:modified_time', content: updated });
  if (category) metaTags.push({ name: 'article:section', content: category });
  if (tags) metaTags.push({ name: 'article:tag', content: tags });

  return metaTags;
};

const mapStateToProps = state => ({
  urlLocale: getLocaleCode(state),
  lang: getLanguage(state),
});

/**
 * @TODO
 *  - add schema
 *  - add i18n translation
 */
const SEOHead = ({
  lang,
  schema,
  title,
  seoImage,
  description,
  path,
  contentType,
  published,
  updated,
  category,
  tags,
  urlLocale,
  robots,
  structData,
}) => (
  <Helmet
    htmlAttributes={{
      lang,
      itemscope: undefined,
      itemtype: `http://schema.org/${schema}`,
    }}
    title={title}
    link={[
      { rel: 'canonical', href: `${SNAPASK_SITE_URL}/${urlLocale}/${path}` },
    ]}
    meta={getMetaTags({
      title,
      description,
      seoImage,
      contentType,
      path,
      published,
      updated,
      category,
      tags,
      urlLocale,
      robots,
    })}
  >
    {!!structData && (
      <script type="application/ld+json">{JSON.stringify(structData)}</script>
    )}
  </Helmet>
);

SEOHead.propTypes = {
  category: string,
  contentType: string,
  description: string,
  lang: string,
  path: string,
  published: string,
  robots: string,
  schema: string,
  seoImage: string,
  structData: oneOfType([object, array]),
  tags: string,
  title: string,
  updated: string,
  urlLocale: string,
};

SEOHead.defaultProps = {
  contentType: 'website',
  title: 'Snapask',
  description:
    'We empower students to learn confidently and succeed in life. Join us to redefine what it means to learn, and what it means to teach. See Team Openings.', // eslint-disable-line max-len
  path: '',
  category: '',
  published: '',
  updated: '',
  tags: '',
  seoImage: '',
  schema: 'Service',
  lang: DEFAULT_LOCALE,
  urlLocale: DEFAULT_LOCALE,
  robots: 'index, follow',
  structData: undefined,
};

export default connect(mapStateToProps, null)(SEOHead);



// WEBPACK FOOTER //
// ./app/components/SEOHead/SEOHead.jsx